import { defineStore } from "pinia";

import paymentSourcesApi from "@/services/api/paymentSourcesApi";

import { getCompanyId } from "@/utils/userUtils";

export const usePaymentSourceStore = defineStore(
  "paymentSourceStore",
  {
    state: () => ({
      sources: [],
      companyId: getCompanyId(),
      isLoading: false,
      error: null,

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },

      selectItems: [],
      selectSearchQuery: "",
      selectPagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },
      selectIsLoading: false,
    }),

    actions: {
      async getSourcesRequest(params = {}) {
        this.isLoading = true;
        try {
          const response = await paymentSourcesApi.index(
            this.companyId,
            params
          );
          this.sources = response.data.result || response.data || [];

          const meta = response.data.pagination_meta;
          if (meta) {
            this.pagination = {
              page: meta.page,
              pages: meta.pages,
              series: meta.series,
              next: meta.next,
              prev: meta.prev,
              count: meta.count,
              limit: meta.limit,
            };
          }
        } catch (error) {
          this.error = error;
          console.error(
            "Ошибка при получении источников оплаты:",
            error
          );
        } finally {
          this.isLoading = false;
        }
      },

      async showSource(id) {
        try {
          const response = await paymentSourcesApi.show(
            this.companyId,
            id
          );
          return response.data.result || response.data || {};
        } catch (error) {
          this.error = error;
          console.error(
            "Ошибка при получении источника оплаты:",
            error
          );
        }
      },

      async createSource(payload) {
        this.isLoading = true;
        try {
          const response = await paymentSourcesApi.create(
            this.companyId,
            payload
          );
          await this.getSourcesRequest();
          return response.data.result || response.data || {};
        } catch (error) {
          console.error(
            "Ошибка при создании источника оплаты:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async updateSource(id, payload) {
        this.isLoading = true;
        try {
          await paymentSourcesApi.update(this.companyId, id, payload);
          await this.getSourcesRequest();
        } catch (error) {
          console.error(
            "Ошибка при обновлении источника оплаты:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async sourceEnable(id) {
        this.isLoading = true;
        try {
          await paymentSourcesApi.enable(this.companyId, id);
          await this.getSourcesRequest();
        } catch (error) {
          console.error(
            "Ошибка при включении источника оплаты:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async sourceDisable(id) {
        this.isLoading = true;
        try {
          await paymentSourcesApi.disable(this.companyId, id);
          await this.getSourcesRequest();
        } catch (error) {
          console.error(
            "Ошибка при отключении источника оплаты:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async fetchSelectItems(payload = {}) {
        this.selectSearchQuery = payload.search || "";
        return await this.getSelectItemsRequest(1);
      },

      async loadMoreSelectItems() {
        if (this.selectIsLoading || !this.selectPagination.next)
          return;
        const nextPage = this.selectPagination.page + 1;
        return await this.getSelectItemsRequest(nextPage);
      },

      async getSelectItemsRequest(page) {
        const pageNumber = parseInt(page) || 1;
        this.selectIsLoading = true;
        try {
          const params = {
            page: pageNumber,
            enabled: true,
          };

          if (this.selectSearchQuery) {
            params["q[search]"] = this.selectSearchQuery;
          }

          const response = await paymentSourcesApi.index(
            this.companyId,
            params
          );
          const result = response.data.result || response.data || [];

          if (pageNumber === 1) {
            this.selectItems = result;
          } else {
            this.selectItems = [...this.selectItems, ...result];
          }

          const meta = response.data.pagination_meta;
          if (meta) {
            this.selectPagination = {
              page: meta.page,
              pages: meta.pages,
              series: meta.series,
              next: meta.next,
              prev: meta.prev,
              count: meta.count,
              limit: meta.limit,
            };
          }
        } catch (error) {
          console.error(
            "Ошибка при загрузке источников оплаты для селекта:",
            error
          );
        } finally {
          this.selectIsLoading = false;
        }
      },

      clearSelectItems() {
        this.selectItems = [];
        this.selectSearchQuery = "";
        this.selectPagination = {
          page: 1,
          pages: 1,
          series: [],
          next: null,
          prev: null,
          count: 0,
          limit: 40,
        };
      },
    },
  }
);
